import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { ApiClient } from '@app/lib/api/api-client';
import {
  GetAffiliateDetailParams,
  GetAffiliateDetailResponse,
  Moderator,
  ModeratorRequest,
  ModeratorResponse,
  ModeratorRole,
  UserInfoResponse,
  UserManagementResponse
} from '@app/lib/api/user/api.user.model';
import { UserManagementReport } from '@app/shared/models/users-management';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  userInfo = new BehaviorSubject<any>({});

  private indexTab: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  getIndexTab(): Observable<number> {
    return this.indexTab.asObservable();
  }

  setIndexTab(newState: number): void {
    this.indexTab.next(newState);
  }

  private api: ApiClient;
  constructor(private http: HttpClient) {
    this.api = new ApiClient(this.http, { responseTimeout: environment.API_TIMEOUT });
  }

  getUserById(id: string): Observable<UserInfoResponse> {
    return this.api.user.getUserById(id);
  }

  getTotalsUser(roles: any, statuses: any, keyword: string) {
    return this.api.user.getTotalsUser(roles, statuses, keyword);
  }

  getById(id: string): Observable<any> {
    return this.http.get<any>('assets/mock/user-by-id.json');
  }

  updateUser(userId: string, userDetail: Partial<any>): Observable<any> {
    return this.api.user.updateUser(userId, userDetail);
  }

  delete(id: string) {
    return this.api.user.delete(id);
  }

  getUserManagementReport(): Observable<UserManagementReport> {
    return this.api.user.getUserManagementReport();
  }

  getUserManagement(
    pageNum: number,
    pageSize: number,
    textSearch: string,
    filterData?: any
  ): Observable<UserManagementResponse> {
    return this.api.user.getUserManagement(pageNum, pageSize, textSearch, filterData);
  }

  getCountries(): Observable<any> {
    return this.api.user.getCountries();
  }

  getModerateRole(body: ModeratorRequest): Observable<string> {
    return this.api.user.getModerateRole(body);
  }

  addModerator(body: ModeratorRequest): Observable<Moderator[]> {
    return this.api.user.addModerator(body);
  }

  getModerators(
    pageNum = 0,
    pageSize = 10,
    nodeId: string,
    param: ModeratorRequest
  ): Observable<ModeratorResponse> {
    return this.api.user.getModerators(pageNum, pageSize, nodeId, param);
  }

  getModeratorRole(): Observable<ModeratorRole[]> {
    return this.api.user.getModeratorRole();
  }

  updateUserBadge(userId: string, badge: string, isUpdate: boolean, body: any): Observable<any> {
    return this.api.user.updateUserBadge(userId, badge, isUpdate, body);
  }

  deleteUserBadge(userId: string, badge: string, body: any): Observable<any> {
    return this.api.user.deleteUserBadge(userId, badge, body);
  }

  getAffiliateMarketerGroups(): Observable<any> {
    return this.api.user.getAffiliateMarketerGroups();
  }

  getAffiliateMarketerUsers(
    groupId: string,
    isAddUser: boolean,
    pageNum: number,
    pageSize: number,
    textSearch: string
  ): Observable<any> {
    return this.api.user.getAffiliateMarketerUsers(
      groupId,
      isAddUser,
      pageNum,
      pageSize,
      textSearch
    );
  }

  getAffiliateMarketerUserDetail(
    groupId: string,
    userId: string,
    pageNum: number,
    pageSize: number,
    textSearch: string,
    startTime: string,
    endTime: string
  ): Observable<any> {
    return this.api.user.getAffiliateMarketerUserDetail(
      groupId,
      userId,
      pageNum,
      pageSize,
      textSearch,
      startTime,
      endTime
    );
  }

  addAffiliateMarketer(groupId: string, userIdList: string[]): Observable<any> {
    return this.api.user.addAffiliateMarketer(groupId, userIdList);
  }

  deleteAffiliateMarketer(groupId: string, userId: string): Observable<any> {
    return this.api.user.deleteAffiliateMarketer(groupId, userId);
  }

  getUserGrowth(
    countryCode: string,
    viewBy: string,
    startTime: string,
    endTime: string
  ): Observable<any> {
    return this.api.user.getUserGrowth(countryCode, viewBy, startTime, endTime);
  }

  getUserPostGrowth(
    countryCode: string,
    viewBy: string,
    startTime: string,
    endTime: string
  ): Observable<any> {
    return this.api.user.getUserPostGrowth(countryCode, viewBy, startTime, endTime);
  }

  getUserEngagement(
    countryCode: string,
    viewBy: string,
    startTime: string,
    endTime: string
  ): Observable<any> {
    return this.api.user.getUserEngagement(countryCode, viewBy, startTime, endTime);
  }

  getVerifiedUserOverview(
    countryCode: string,
    viewBy: string,
    startTime: string,
    endTime: string
  ): Observable<any> {
    return this.api.user.getVerifiedUserOverview(countryCode, viewBy, startTime, endTime);
  }

  getUserByAge(
    countryCode: string,
    startDateEpochMillis: number,
    endDateEpochMillis: number
  ): Observable<any> {
    return this.api.user.getUserByAge(countryCode, startDateEpochMillis, endDateEpochMillis);
  }

  getUserByGender(countryCode: string, startTime: string, endTime: string): Observable<any> {
    return this.api.user.getUserByGender(countryCode, startTime, endTime);
  }

  getUserTopXu(countryCode: string, startTime: string, endTime: string): Observable<any> {
    return this.api.user.getUserTopXu(countryCode, startTime, endTime);
  }

  getUserTopFollower(countryCode: string, startTime: string, endTime: string): Observable<any> {
    return this.api.user.getUserTopFollower(countryCode, startTime, endTime);
  }

  getUserSummary(countryCode: string, startTime: string, endTime: string): Observable<any> {
    return this.api.user.getUserSummary(countryCode, startTime, endTime);
  }

  getAffiliateDetails(params: GetAffiliateDetailParams): Observable<GetAffiliateDetailResponse> {
    return this.api.user.getAffiliateDetails(params);
  }

  getPermissionCodeList() {
    return this.api.user.getPermissionCodeList();
  }
}
